import { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Switch from 'src/components/Switch/Switch';
import { PageCardLoader } from 'src/components/PageCardLayout';
import { AlertType, useAlert } from 'src/components/AlertsProvider';
import { pepApiRequest, RequestMethod } from 'src/services';
import { useFeatureFlagsContext } from 'src/contexts/FeatureFlagsContext';

function FeatureToggles() {
  const { flags, loading, refetch } = useFeatureFlagsContext();
  const [togglingFeature, setTogglingFeature] = useState<string | null>(null);
  const { showAlert } = useAlert();

  const handleToggle = async (featureName: string, enabled: boolean) => {
    setTogglingFeature(featureName);
    try {
      await pepApiRequest({
        method: RequestMethod.Put,
        url: `/features/${encodeURIComponent(featureName)}/state`,
        useProxy: false,
        params: { enabled },
      });
      await refetch();
      showAlert({
        type: AlertType.Info,
        title: 'Feature updated',
        text: `Feature "${featureName}" has been ${enabled ? 'enabled' : 'disabled'} and it will reflect in few seconds.`,
      });
    } catch (error) {
      showAlert({
        type: AlertType.Error,
        title: 'Error',
        text: `Failed to update feature "${featureName}".`,
      });
    } finally {
      setTogglingFeature(null);
    }
  };

  if (loading) {
    return <PageCardLoader />;
  }

  const featureEntries = flags ? Object.entries(flags) : [];

  return (
    <Box>
      {featureEntries.length === 0 && (
        <Typography variant="body2" color="textSecondary">
          No feature toggles available.
        </Typography>
      )}
      {featureEntries.map(([name, enabled]) => (
        <Box
          key={name}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          py={1}
          borderBottom="1px solid rgba(0, 0, 0, 0.08)"
        >
          <Box>
            <Typography variant="body1" fontWeight="500">
              {name}
            </Typography>
          </Box>
          <Switch
            value={enabled}
            onChange={({ target }) => {
              handleToggle(name, target.checked);
            }}
            disabled={togglingFeature === name}
          />
        </Box>
      ))}
      {togglingFeature && <PageCardLoader />}
    </Box>
  );
}

export default FeatureToggles;
